import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ApplicationRoles, PagedResponse, SessionService, User } from '@edl-development/edl-common';
import { StateService } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { A48_STATUS_FILTERS, SUBMISSIONS_MY_ITEMS_STATUS_FILTERS, SUBMISSIONS_SUPERVISOR_STATUS_FILTERS } from 'src/app/constants/roster.constants';
import { UserRequestFilterStatus } from 'src/app/enums/user-request/user-request-filter-status.enum';
import { EdlSelectOption } from 'src/app/model/common/edl-select-option.model';
import { CplRequestItem } from 'src/app/model/cpl-request-item.model';
import { RequestVerificationStatus } from 'src/app/requests/enums/verification-status.enum';
import { RequestHelpersService } from 'src/app/services/request-helpers/request-helpers.service';
import { UserRequestService } from 'src/app/services/user-request.service';
import { UserRequestActionsTypes } from 'src/app/shared-request-items/enum/user-request-actions-types.enum';
import { RequestTypeFilter } from '../../enums/request-type-filter.enum';
import { SubmissionActionType } from '../../enums/submission-action-type.enum';

@Component({
    selector: 'edl-submissions',
    templateUrl: './submissions.component.html',
    styleUrls: ['./submissions.component.scss']
})
export class SubmissionsComponent implements OnInit {
    @Input() selectedRequest: CplRequestItem;
    @Input() currentPage: number;
    @Input() user: User;
    @Input() isMyItems: boolean;

    form: UntypedFormGroup;
    pagedResponse: PagedResponse;
    activeActionColumns: SubmissionActionType[] = [];

    isLoading: boolean = false;
    filterStatuses: EdlSelectOption<UserRequestFilterStatus | null>[];
    A48_STATUS_FILTERS = A48_STATUS_FILTERS;
    RequestTypeFilter = RequestTypeFilter;
    ApplicationRoles = ApplicationRoles;

    UserRequestActionsTypes = UserRequestActionsTypes;

    private lastSelectInteraction: 'mouse' | null = null;


    constructor(
        private formBuilder: UntypedFormBuilder,
        private userRequestService: UserRequestService,
        private sessionService: SessionService,
        private toastr: ToastrService,
        private transition: Transition,
        private stateService: StateService,
        private requestHelpers: RequestHelpersService
    ) { }

    ngOnInit() {
        this.filterStatuses = this.isMyItems
            ? SUBMISSIONS_MY_ITEMS_STATUS_FILTERS
            : SUBMISSIONS_SUPERVISOR_STATUS_FILTERS;
        this.initializeForm();
        this.loadSubmissions(this.selectedRequest);
    }

    private initializeForm() {
        const { searchBy, status, act48Status, approverUserId, groupIds, requestType } = this.transition.params();

        this.form = this.formBuilder.group({
            Name: [searchBy ?? '', []],
            Status: [status === undefined ? UserRequestFilterStatus.Active : +status, []],
            Act48Status: [act48Status ?? null, []],
            ApproverUserId: [approverUserId ?? null, []],
            GroupIDs: [(Array.isArray(groupIds) ? groupIds : [groupIds]) ?? [], []],
            searchBy: [searchBy ?? '', []],
            requestType: [requestType ?? RequestTypeFilter.MyRequests, []]
        });
    }

    private loadSubmissions(request: CplRequestItem) {
        if (!request) return;
        this.isLoading = true;
        const pageSize = this.sessionService.pageSize() || 20;
        const searchBy = this.form.value.Name || this.form.value.searchBy || null;
        const currentPage = this.currentPage || 1;
        const { orderBy, ascending, userArchivedFilter } = this.transition.params();

        const requestParams = {
            requestId: request.RequestID,
            offset: (currentPage - 1) * pageSize,
            limit: pageSize,
            status: this.form.value.Status,
            orderBy: orderBy,
            ascending: ascending === 'true',
            act48Status: this.form.value.Act48Status,
            approverUserId: this.form.value.ApproverUserId,
            groupIds: this.form.value.GroupIDs || [],
            searchBy: searchBy,
            targetUserId: this.user.UserID,
            requestType: this.form.value.requestType || RequestTypeFilter.MyRequests,
            userArchivedFilter: userArchivedFilter != null ? Number(userArchivedFilter) : undefined
        };

        const requestObservable = this.isMyItems
            ? this.userRequestService.getUserRequestsForItemBasicUser(requestParams)
            : this.userRequestService.getUserRequestsForItem(requestParams);

        requestObservable.subscribe({
            next: (response) => {
                this.pagedResponse = response;
                this.determineActiveActionColumns();
                this.isLoading = false;
            },
            error: (error) => {
                this.toastr.error('Error loading submissions: ' + (error.message || error));
                this.isLoading = false;
            }
        });
    }

    private determineActiveActionColumns() {
        const activeActions = new Set<SubmissionActionType>();

        if (!this.isMyItems) {
            const hasApprove = this.pagedResponse.Results.some(userRequest =>
                this.requestHelpers.isRequestItemActive(this.selectedRequest) &&
                this.requestHelpers.canPerformVerificationAction(userRequest, this.selectedRequest, RequestVerificationStatus.APPROVED)
            );
            if (hasApprove) activeActions.add(SubmissionActionType.APPROVE);
        }

        const hasView = this.pagedResponse.Results.some(userRequest =>
            this.requestHelpers.canViewUserRequestPage(userRequest)
        );
        if (hasView) activeActions.add(SubmissionActionType.VIEW);

        this.activeActionColumns = Array.from(activeActions);
    }

    search(form: UntypedFormGroup) {
        const searchValue = form.value.searchBy || '';
        const groupIdsValue = form.value.GroupIDs && form.value.GroupIDs.length > 0 ? form.value.GroupIDs : undefined;

        const params = {
            requestId: this.selectedRequest.RequestID,
            page: 1,
            searchBy: searchValue || undefined,
            status: form.value.Status || -1,
            act48Status: form.value.Act48Status || undefined,
            approverUserId: form.value.ApproverUserId || undefined,
            groupIds: groupIdsValue,
            requestType: form.value.requestType || RequestTypeFilter.MyRequests
        };

        // Context aware navigation
        if (this.stateService.includes('authenticated.all-users-progress')) {
            // supervision management context
            const userId = this.transition.params().userId;
            this.stateService.go('authenticated.all-users-progress.user-compliance.details.submissions', {
                ...params,
                userId: userId
            });
        } else {
            // my items context
            this.stateService.go('authenticated.my-items.submissions', params);
        }
    }

    handleSearchKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.search(this.form);
        }
    }

    handleStatusKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.search(this.form);
        }
    }

    onSelectMouseDown() {
        this.lastSelectInteraction = 'mouse';
    }

    onDropdownChange(event: Event) {
        if (this.lastSelectInteraction === 'mouse') {
            this.search(this.form);
        }
        this.lastSelectInteraction = null;
    }


    public get canViewRequestType() {
        const userRole = this.user?.AccessRoleID;
        return userRole === this.ApplicationRoles.ADMIN
            || userRole === this.ApplicationRoles.EDLADMIN
            || userRole === this.ApplicationRoles.SUPERVISOR
            || userRole === this.ApplicationRoles.LIMITED_SUPERVISOR;
    }
}